import { SitesBaseIcon } from '@tableau/icons/SitesBaseIcon';
import { MenuItemInfo, Tooltip } from '@tableau/widgets';
import moment from 'moment';
import React from 'react';
import { style } from 'typestyle';

import { Messages } from '~src/codegen/Localize';
import { CommonStyles } from '~src/commonInfra/styles/CommonStyles';

import { AlertDetailsPopover } from '../dataConnectAlerts/AlertDetailsPopover';
import { ClusterNodePresenceIcon } from '../dataConnectClusterNodes/ClusterNodePresenceIcon';
import { getClusterNodeStatus } from '../dataConnectClusterNodes/ClusterNodeStates';
import { ClusterNodeStatus } from '../dataConnectClusterNodes/ClusterNodeStatus';
import { textCellPadding } from '../dataConnectMultiCluster/DataConnectMultiClusterTableHeaders';
import { openAddPoolDomainDialog } from '../dataConnectNetworkAllowlist/addNewDomainDialog/AddNewDomainDialog';
import { AddPoolDomainButton } from '../dataConnectNetworkAllowlist/AddPoolDomainButton';
import { ActionItemInfo, ActionItemType, ActionType, MoreActionsMenu } from '../MoreActionsMenu';
import { Domain } from '../types/Domain';
import { UserDefinedPoolModel } from '../types/PoolModel';
import { AssignClusterButton } from './AssignClusterButton';
import { openDeletePoolDialog } from './DeletePoolDialog';
import { openEditPoolDialog } from './EditPoolDialog';
import { PoolNamePopover } from './PoolNamePopover';
import { openRefreshBaseImageDialog } from './RefreshBaseImageAction';
import { openRefreshTokensDialog } from './RefreshTokensDialog';

export type MultiPoolTableEntryPoolProps = Readonly<{
  pool: UserDefinedPoolModel;
  existingPoolNames: ReadonlyArray<string>;
  isExpanded: boolean;
  siteId: string;
  userDefinedPools: Record<string, UserDefinedPoolModel>;
  bridgeDomains: Record<string, Domain>;
  refreshPoolsAndClusters: () => void;
}>;

export function DataConnectMultiPoolTableEntryPool(props: MultiPoolTableEntryPoolProps) {
  const { pool, isExpanded, siteId, userDefinedPools, bridgeDomains, refreshPoolsAndClusters } = props;

  let poolCellStyle =  textCellPadding + ' tb-data-grid-separator-row ';

  poolCellStyle += isExpanded ? expandedRowFocus : '';

  const targetSelectItem: MenuItemInfo[] = [
    { displayValue: Messages.dataConnectSiteSettingsEditPoolNameButton(), value: ActionType.EDIT_POOL_NAME },
    { displayValue: Messages.dataConnectSiteSettingsRefreshTokensButton(), value: ActionType.REFRESH_TOKEN, disabled: pool.clusterId === undefined && pool.clusterId === ''  },
    { displayValue: Messages.dataConnectSiteSettingsRefreshBaseImageButton(), value: ActionType.REFRESH_BASE_IMAGE, disabled: disableRefreshBaseImageButton(pool.status) },
    { displayValue: Messages.deleteMessage(), value: ActionType.DELETE },
  ];

  if (pool.clusterId === undefined || pool.clusterId === '') {
    targetSelectItem.unshift({ displayValue: Messages.dataConnectSiteSettingsAddNewDomainButton(), value: ActionType.ADD_POOL_DOMAIN });
  }
  const poolNodeStatus = getClusterNodeStatus();
  return <>
    <td className={poolCellStyle}  style={{paddingLeft:0}}>
      <PoolNamePopover pool={pool}/>
    </td>
    <td className={poolCellStyle} >
      <span className={'tb-data-grid-cell-text-data-connect tb-lr-padded-wide'} data-tb-test-id={'pool-status-cell'}>
        {<ClusterNodePresenceIcon status={pool.status} clusterNodeStatus={poolNodeStatus} />}
        {poolNodeStatus[pool.status].stateLabel}
      </span>
    </td>
    <td className={poolCellStyle} style={{paddingLeft: 0}}>
      <span className={'tb-data-grid-cell-text-data-connect tb-lr-padded-wide'} data-tb-test-id={'pool-alerts-column-cell'}>
        <AlertDetailsPopover  alerts={pool.alerts} siteId={siteId} />
      </span>
    </td>
    <td className={poolCellStyle}>
      <span className={'tb-data-grid-cell-text-data-connect tb-lr-padded-wide'} style={{paddingRight: 0}} data-tb-test-id={'pool-clustername-column-cell'}>
        {pool.clusterName}
      </span>
      <span style={{paddingLeft: 4, verticalAlign: 'text-top'}}>
        { pool.sharedCluster && <>
          <Tooltip children={<SitesBaseIcon size={12} alt={''} />}
               tooltipContent={Messages.dataConnectSiteSettingsClusterShared()}
               position={Tooltip.defaultProps.position!}
               afterDelay={false}
               followCursor={false} />
          </>}
      </span>
    </td>
    <td className={poolCellStyle}>
      <span className={'tb-data-grid-cell-text-data-connect tb-lr-padded-wide'} data-tb-test-id={'pool-domain-column-cell'}>
        {Object.values(pool.domains).length}
      </span>
    </td>
    <td className={poolCellStyle}>
      <span className={'tb-data-grid-cell-text-data-connect tb-lr-padded-wide'} data-tb-test-id={'pool-token-column-cell'}>
        { getTokenExpiresDate(pool.tokenExpiresAt) }
      </span>
    </td>
    <td className={poolCellStyle}>
      <span className={'tb-data-grid-cell-text-data-connect tb-lr-padded-wide'} data-tb-test-id={'pool-token-column-cell'}>
        { getImageBuildDate(pool.imageBuildAt) }
      </span>
    </td>
    <td className={poolCellStyle}>
      <span className={'tb-data-grid-cell-text-data-connect tb-lr-padded-wide'} data-tb-test-id={'pool-token-column-cell'}>
        { getDeployDate(pool.deployAt) }
      </span>
    </td>
    <td className={poolCellStyle}>
      <span className={'tb-data-grid-cell-text-data-connect tb-lr-padded-wide'} data-tb-test-id={'pool-status-cell'}>
        {(pool.clusterId === undefined || pool.clusterId === '')
         && <AssignClusterButton poolId={pool.poolId} poolName={pool.displayName} siteId={siteId}  refreshPoolsAndClusters={refreshPoolsAndClusters} />
        }
        {(pool.clusterId !== undefined && pool.clusterId !== '')
          && <AddPoolDomainButton poolId={pool.poolId} poolName={pool.displayName}   siteId={siteId} userDefinedPools={userDefinedPools} bridgeDomains={bridgeDomains} refreshPoolsAndClusters={refreshPoolsAndClusters} />
        }
        <MoreActionsMenu
          targetItemInfo={targetSelectItem}
          actionType= {ActionItemType.NODE_ACTION}
          actionItemInfo={{poolId: pool.poolId, poolName: pool.displayName,
                        clusterId: pool.clusterId,
                        domains: pool.domains,
                        userDefinedPools: userDefinedPools,
                        bridgeDomains: bridgeDomains,
                        siteId: siteId, refreshPoolsAndClusters: refreshPoolsAndClusters}}
          onHandleMenuSelect={onHandleMenuSelect}
        />
       </span>
    </td>
  </>;
}


const onHandleMenuSelect = (actionItem: ActionItemInfo, actionType: string) => {
  if (actionType === ActionType.DELETE) {
    openDeletePoolDialog(actionItem, actionItem.refreshPoolsAndClusters);
  } else if (actionType === ActionType.EDIT_POOL_NAME) {
    openEditPoolDialog(actionItem, actionItem.refreshPoolsAndClusters);
  } else if (actionType === ActionType.REFRESH_TOKEN) {
    openRefreshTokensDialog(actionItem);
  } else if (actionType === ActionType.REFRESH_BASE_IMAGE) {
    openRefreshBaseImageDialog(actionItem);
  } else if (actionType === ActionType.ADD_POOL_DOMAIN) {
    openAddPoolDomainDialog(actionItem.poolId, actionItem.poolName, actionItem.siteId, actionItem.userDefinedPools,
                            actionItem.bridgeDomains, actionItem.refreshPoolsAndClusters);
  }
};

export const expandedRowFocus = style({
    backgroundColor: CommonStyles.Colors.BlueHighlight
  });

const disableRefreshBaseImageButton = (poolStatus: ClusterNodeStatus) => {
  return poolStatus !== ClusterNodeStatus.AVAILABLE;
};

const getTokenExpiresDate = (tokenExpiresAt: Date) => {
  if (tokenExpiresAt === undefined) {
    return Messages.dataConnectSiteSettingsPoolNoTokenCreated();
  }
  const date = moment(tokenExpiresAt);
  const formattedDate = date.format('MMM D, YYYY, h:mm A');
  return formattedDate;
};
const getImageBuildDate = (imageBuildAt: Date) => {
  if (imageBuildAt === undefined) {
    return Messages.dataConnectSiteSettingsPoolNoImageBuild();
  }
  return formatDate(imageBuildAt);
};

const getDeployDate = (deployAt: Date) => {
  if (deployAt === undefined) {
    return Messages.dataConnectSiteSettingsPoolNoDeployment();
  }
  return formatDate(deployAt);
};
 const formatDate =(inputDate: Date) => {
  const date = moment(inputDate);
  const formattedDate = date.format('MMM D, YYYY, h:mm A');
  return formattedDate;
 };